import { Config, driver } from 'driver.js';
import '../../../../public/driver-styles.scss';

import { FlowStep, FlowType, TourStep } from 'src/frontend/constants/GettingStartedFlows';

import { ComponentInstanceService } from '@diagram/services/ComponentInstanceService';
import { ConnectionService } from '@diagram/services/ConnectionService';
import { DiagramService } from '@diagram/services/DiagramService';
import { gettingStartedState } from '@diagram/state/getting-started';
import { gettingStartedTourState } from '@diagram/state/getting-started-tour';

import { SlackChannel } from 'models';

import { mountTourBadge } from 'components/tour/TourBadge';
import { ExpertSupportDriverService } from 'services/drivers/ExpertSupportDriverService';
import { FaultSimDriverService } from 'services/drivers/FaultSimDriverService';
import { PowerFlowSimDriverService } from 'services/drivers/PowerFlowSimDriverService';
import { WireSizingDriverService } from 'services/drivers/WireSizingDriverService';
import { InternalTrackingService } from 'services/InternalTrackingService';

const GET_ELEMENT_RETRY_ATTEMPTS = 20;
const GET_ELEMENT_RETRY_DELAY = 150;

const DriverService = {
    request: (tour: FlowType) => {
        gettingStartedTourState.activeTour = tour;
    },

    start: (tour: FlowType) => {
        InternalTrackingService.track(
            'tour-started',
            { tour },
            {
                notifySlack: true,
                channel: SlackChannel.LOGGING,
            },
        );

        DriverService.resetTrackedElements();

        switch (tour) {
            case FlowType.EXPERT_SUPPORT:
                ExpertSupportDriverService.start();
                break;
            case FlowType.FAULT_SIMULATIONS:
                FaultSimDriverService.start();
                break;
            case FlowType.POWER_FLOW_SIMULATIONS:
                PowerFlowSimDriverService.start();
                break;
            case FlowType.WIRE_SIZING:
                WireSizingDriverService.start();
                break;
            default:
                break;
        }
    },

    showable: (tour: FlowType) => {
        return gettingStartedTourState.activeTour === tour;
    },

    // A tour only reacts to what the user adds while it runs, so remember what the
    // diagram already held and drop whatever a previous run of a tour tracked.
    resetTrackedElements: () => {
        const { componentInstances, connections } = DiagramService.get();

        gettingStartedTourState.preTourComponentInstanceIds = Object.keys(componentInstances);
        gettingStartedTourState.preTourConnectionIds = Object.keys(connections);
        gettingStartedTourState.tourComponentInstanceIds = [];
        gettingStartedTourState.tourConnectionIds = [];
        gettingStartedTourState.tourTargetConnectionId = null;
    },

    cancel: (tour: FlowType) => {
        if (gettingStartedTourState.activeTour === tour) {
            InternalTrackingService.track('tour-stopped', {
                tour,
                step: gettingStartedTourState.activeTourStep,
            });

            gettingStartedTourState.activeTour = null;
        }
    },

    finish: (tour: FlowType, markAsComplete: boolean = true) => {
        if (markAsComplete) {
            InternalTrackingService.track('tour-finished', { tour });
        } else {
            InternalTrackingService.track('tour-stopped', {
                tour,
                step: gettingStartedTourState.activeTourStep,
            });
        }

        if (gettingStartedTourState.activeTour === tour) {
            gettingStartedTourState.activeTour = null;
        }

        if (markAsComplete && !gettingStartedTourState.completedTours.includes(tour)) {
            gettingStartedTourState.completedTours = [...gettingStartedTourState.completedTours, tour];
        }
    },

    isCompleted: (tour: FlowType) => {
        return gettingStartedTourState.completedTours.includes(tour);
    },

    setup: ({ tour, ...config }: Config & { tour?: FlowType }) => {
        if (!tour) {
            return driver(config);
        }

        let unmountBadge: (() => void) | null = null;

        const removeBadge = () => {
            unmountBadge?.();
            unmountBadge = null;
        };

        return driver({
            ...config,
            onPopoverRender: (popover, options) => {
                removeBadge();

                const container = document.createElement('div');
                container.className = 'tour-badge';
                popover.title.before(container);

                unmountBadge = mountTourBadge(tour, container);

                config.onPopoverRender?.(popover, options);
            },
            onDestroyed: (element, step, options) => {
                removeBadge();

                config.onDestroyed?.(element, step, options);
            },
        });
    },

    getCurrentStep: () => gettingStartedTourState.activeTourStep,

    setStep: (tour: FlowType, step: TourStep) => {
        gettingStartedTourState.activeTour = step ? tour : null;
        gettingStartedTourState.activeTourStep = step;
    },

    advance: (flowType: FlowType, sequence: TourStep[]) => {
        if (!DriverService.showable(flowType)) {
            return;
        }

        const currentStep = gettingStartedTourState.activeTourStep;
        if (!currentStep) return;

        const currentIndex = sequence.indexOf(currentStep);
        if (currentIndex === -1 || currentIndex >= sequence.length - 1) return;

        const nextStep = sequence[currentIndex + 1];
        gettingStartedTourState.activeTourStep = nextStep;
        if (!nextStep) {
            gettingStartedTourState.activeTour = null;
        }
    },

    showHighlight: (
        flowKey: FlowType,
        config: FlowStep,
        options?: {
            onAdvance?: () => void;
        },
    ) => {
        if (!DriverService.showable(flowKey)) {
            return Promise.resolve(() => {
                // noop
            });
        }

        return new Promise<() => void>((resolve) => {
            let cancelled = false;
            let timeout: ReturnType<typeof setTimeout> | null = null;
            let driverInstance: ReturnType<typeof DriverService.setup> | null = null;
            let elementObserver: MutationObserver | null = null;

            const hideDriver = () => {
                driverInstance?.destroy();
                driverInstance = null;
            };

            const teardown = () => {
                elementObserver?.disconnect();
                elementObserver = null;
                hideDriver();
            };

            const showDriver = (element: Element | null) => {
                if (cancelled || driverInstance) {
                    return;
                }

                driverInstance = DriverService.setup({
                    animate: false,
                    allowClose: false,
                    tour: flowKey,

                    ...(config.popoverOffset ? { popoverOffset: config.popoverOffset } : {}),
                    steps: [
                        {
                            ...(element ? { element } : {}),
                            popover: {
                                showButtons: ['close', 'next'],
                                nextBtnText: config.isLastStep
                                    ? 'Run another tour'
                                    : config.showNextButton
                                      ? 'Next →'
                                      : '',
                                ...config.popover,
                            },
                        },
                    ],
                    onCloseClick: (_, __, { driver }) => {
                        gettingStartedTourState.popoverIsShown = false;
                        DriverService.finish(flowKey, config.isLastStep || false);
                        driver.destroy();
                    },
                    onNextClick: (_, __, { driver }) => {
                        gettingStartedTourState.popoverIsShown = false;
                        if (config.isLastStep) {
                            DriverService.finish(flowKey, config.isLastStep || false);
                            driver.destroy();
                            gettingStartedState.open = true;
                        } else {
                            options?.onAdvance?.();
                        }
                    },
                    onPopoverRender: (popover, { driver }) => {
                        gettingStartedTourState.popoverIsShown = true;
                        const buttonElement = document.createElement('button');

                        if (config.isLastStep) {
                            buttonElement.innerText = 'Done';
                            buttonElement.dataset.done = 'true';
                        } else {
                            buttonElement.innerText = 'Stop tour';
                            buttonElement.dataset.cancel = 'true';
                        }

                        buttonElement.addEventListener('click', () => {
                            DriverService.finish(flowKey, config.isLastStep || false);
                            driver.destroy();
                        });

                        popover.footerButtons.appendChild(buttonElement);
                    },
                    onDeselected: () => {
                        gettingStartedTourState.popoverIsShown = false;
                    },
                    onDestroyed: () => {
                        gettingStartedTourState.popoverIsShown = false;
                    },
                });

                driverInstance.drive();
            };

            const drive = (element: Element | null) => {
                if (cancelled) {
                    return;
                }

                showDriver(element);

                // Keep the popover in sync with its root element: hide/show it
                // if the element is removed/added to the DOM.
                const selector = config.selector;

                if (element && selector && !elementObserver) {
                    elementObserver = new MutationObserver(() => {
                        if (cancelled) {
                            return;
                        }

                        const current = document.querySelector(selector);

                        if (current?.isConnected) {
                            showDriver(current);
                        } else {
                            hideDriver();
                        }
                    });

                    elementObserver.observe(document.body, { childList: true, subtree: true });
                }
            };

            const waitForElement = (attempts: number) => {
                if (cancelled) {
                    return;
                }

                if (!config.selector) {
                    drive(null);
                    return;
                }

                const element = document.querySelector(config.selector);

                if (element) {
                    DriverService.scrollConnectionIntoView(element);

                    setTimeout(
                        () => {
                            drive(element);
                        },
                        300 + (config.delay || 0),
                    );

                    return;
                }

                if (attempts <= 0) {
                    return;
                }

                timeout = setTimeout(() => waitForElement(attempts - 1), GET_ELEMENT_RETRY_DELAY);
            };

            const launch = () => {
                if (cancelled) {
                    return;
                }

                waitForElement(GET_ELEMENT_RETRY_ATTEMPTS);
            };

            launch();

            resolve(() => {
                cancelled = true;

                if (timeout) {
                    clearTimeout(timeout);
                }

                teardown();
            });
        });
    },

    addComponentIntancesHighlightBox: (
        componentInstanceIds: string[],
        options: { dataName: string; padding?: number },
    ) => {
        const { padding = 20, dataName } = options;

        const elements = componentInstanceIds
            .map((id) => document.querySelector(`[data-selectable-id="${id}"]`))
            .filter(Boolean) as Element[];

        const rects = elements.map((el) => el.getBoundingClientRect());
        const top = Math.min(...rects.map((r) => r.top)) - padding;
        const left = Math.min(...rects.map((r) => r.left)) - padding;
        const bottom = Math.max(...rects.map((r) => r.bottom)) + padding;
        const right = Math.max(...rects.map((r) => r.right)) + padding;

        const proxy = document.createElement('div');
        proxy.setAttribute(dataName, 'true');
        proxy.style.position = 'fixed';
        proxy.style.pointerEvents = 'none';
        proxy.style.top = `${top}px`;
        proxy.style.left = `${left}px`;
        proxy.style.width = `${right - left}px`;
        proxy.style.height = `${bottom - top}px`;
        proxy.style.zIndex = '-1';

        document.body.appendChild(proxy);

        return () => {
            proxy.remove();
        };
    },

    hideOnDrag: (showHighlight: () => Promise<(() => void) | null | undefined> | null | undefined) => {
        return new Promise<() => void>((resolve) => {
            let cancelled = false;
            let cleanup: (() => void) | null = null;

            const isDragging = () => document.body.getAttribute('data-dragging') === 'true';

            const show = () => {
                if (cancelled) return;

                showHighlight()?.then((_cleanup) => {
                    if (cancelled) {
                        _cleanup?.();
                        return;
                    }
                    cleanup = _cleanup ?? null;
                });
            };

            const hide = () => {
                cleanup?.();
                cleanup = null;
            };

            const observer = new MutationObserver(() => {
                if (cancelled) return;

                if (isDragging()) {
                    hide();
                    return;
                }

                if (!cleanup) {
                    show();
                }
            });

            observer.observe(document.body, { attributes: true, attributeFilter: ['data-dragging'] });

            if (!isDragging()) {
                show();
            }

            resolve(() => {
                cancelled = true;
                hide();
                observer.disconnect();
            });
        });
    },

    scrollConnectionIntoView: (element: Element) => {
        const connectionHitId = element.getAttribute('data-connection-hit');

        if (!connectionHitId) return;

        const connection = ConnectionService.getById(connectionHitId);

        if (!connection) {
            return;
        }

        ComponentInstanceService.moveIntoView([connection.from.componentInstanceId, connection.to.componentInstanceId]);
    },

    disableNextButton: (disabled: boolean) => {
        const nextBtn = document.querySelector('.driver-popover .driver-popover-next-btn') as HTMLButtonElement | null;

        if (!nextBtn) return;

        nextBtn.dataset.disabled = disabled.toString();
    },
};

export { DriverService };
